import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface PendingReport {
  id: number
  case_id: number
  report_date: string | null
  case_name: string | null
  leader_initials: string | null
  finalized: boolean
  pre_approved: boolean
  approved: boolean
  pending_members: { initials: string; name: string }[]
}

interface Props {
  reports: PendingReport[]
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "#" },
  { title: "Pending Approval", href: "/reports/pending_approval" },
]

function StatusCheck({ passed }: { passed: boolean }) {
  if (passed) {
    return <span className="text-green-600">&#10003;</span>
  }
  return <span className="text-red-500">&#10007;</span>
}

export default function PendingApproval({ reports }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Pending Daily Reports" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Pending Daily Reports</h1>

        {reports.length > 0 ? (
          <div className="border-hpi-light-gray rounded border bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-hpi-light-gray border-b bg-gray-50">
                  <th className="px-4 py-3 text-left font-medium">
                    Report Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Case</th>
                  <th className="px-4 py-3 text-center font-medium">
                    <div>Team</div>
                    <div className="text-xs font-normal text-gray-500">
                      Members
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-medium">
                    <div>Team</div>
                    <div className="text-xs font-normal text-gray-500">
                      Leader
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-medium">
                    <div>Pre</div>
                    <div className="text-xs font-normal text-gray-500">
                      Approval
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-medium">
                    <div>Final</div>
                    <div className="text-xs font-normal text-gray-500">
                      Approval
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.id}
                    className="border-hpi-light-gray border-b last:border-0"
                  >
                    <td className="px-4 py-3">
                      <Link
                        href={`/cases/${report.case_id}/reports/${report.id}`}
                        className="text-hpi-active-blue"
                      >
                        {formatDate(report.report_date)}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/cases/${report.case_id}`}
                        className="text-hpi-active-blue"
                      >
                        {report.case_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        href={`/cases/${report.case_id}/reports/${report.id}`}
                        className="text-hpi-active-blue"
                      >
                        {report.pending_members.length > 0 ? (
                          report.pending_members.map((member, i) => (
                            <div
                              key={i}
                              className="text-red-500"
                              title={member.name}
                            >
                              {member.initials}
                            </div>
                          ))
                        ) : (
                          <StatusCheck passed={true} />
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        href={`/cases/${report.case_id}/reports/${report.id}`}
                        className="text-hpi-active-blue"
                      >
                        {report.finalized ? (
                          <StatusCheck passed={true} />
                        ) : (
                          <span className="text-red-500">
                            {report.leader_initials}
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        href={`/cases/${report.case_id}/reports/${report.id}`}
                        className="text-hpi-active-blue"
                      >
                        <StatusCheck passed={report.pre_approved} />
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        href={`/cases/${report.case_id}/reports/${report.id}`}
                        className="text-hpi-active-blue"
                      >
                        <StatusCheck passed={report.approved} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded border border-green-300 bg-green-50 p-4 text-center text-green-700">
            No pending reports found
          </div>
        )}
      </div>
    </AppLayout>
  )
}
